import { useEffect, useRef } from "react";

export interface EditCohortModalProps {
  readonly open: boolean;
  readonly cohortLabel: string;
  readonly cohortStatus: "active" | "archived";
  readonly disabled: boolean;
  readonly saving: boolean;
  readonly archivingBusy: boolean;
  readonly deletingBusy?: boolean;
  readonly onClose: () => void;
  readonly onLabelChange: (label: string) => void;
  readonly onSaveLabel: () => void | Promise<void>;
  readonly onArchive: () => void | Promise<void>;
  readonly onReactivate: () => void | Promise<void>;
  readonly onDelete?: () => void | Promise<void>;
}

export function EditCohortModal({
  open,
  cohortLabel,
  cohortStatus,
  disabled,
  saving,
  archivingBusy,
  deletingBusy = false,
  onClose,
  onLabelChange,
  onSaveLabel,
  onArchive,
  onReactivate,
  onDelete,
}: EditCohortModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const busy = saving || archivingBusy || deletingBusy;

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="teacher-modal"
      aria-labelledby="edit-cohort-title"
      onClose={onClose}
      onClick={(ev) => {
        if (ev.target === dialogRef.current && !busy) onClose();
      }}
    >
      <div className="teacher-modal-card" role="document">
        <h2 id="edit-cohort-title" className="teacher-modal-title">
          Edit cohort
        </h2>
        <form
          className="stack-form tight"
          onSubmit={(ev) => {
            ev.preventDefault();
            void onSaveLabel();
          }}
        >
          <label className="field">
            <span className="field-label">Cohort label</span>
            <input
              className="input"
              value={cohortLabel}
              onChange={(ev) => onLabelChange(ev.target.value)}
              disabled={disabled || saving}
              required
            />
          </label>
          <div className="teacher-modal-actions">
            <button type="button" className="btn secondary" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={disabled || saving || !cohortLabel.trim()}>
              {saving ? "Saving…" : "Save label"}
            </button>
          </div>
        </form>

        <hr style={{ margin: "1.1rem 0", border: "none", borderTop: "1px solid rgba(27, 77, 46, 0.14)" }} />

        {cohortStatus === "active" ? (
          <div>
            <p className="muted small">
              Archive this cohort for the term. Join codes disable and edits lock until you reactivate.
            </p>
            <button
              type="button"
              className="btn secondary danger"
              disabled={disabled || archivingBusy || saving}
              onClick={() => void onArchive()}
            >
              {archivingBusy ? "Working…" : "Archive cohort"}
            </button>
          </div>
        ) : (
          <div className="stack-form tight">
            <p className="muted small">Reactivate to turn join codes back on and allow edits.</p>
            <button
              type="button"
              className="btn secondary"
              disabled={archivingBusy || saving || deletingBusy}
              onClick={() => void onReactivate()}
            >
              {archivingBusy ? "Working…" : "Reactivate cohort"}
            </button>
            {onDelete ? (
              <>
                <hr style={{ margin: "1.1rem 0", border: "none", borderTop: "1px solid rgba(27, 77, 46, 0.14)" }} />
                <p className="muted small">
                  Permanently remove this cohort and all student progress, scores, and activity history.
                </p>
                <button
                  type="button"
                  className="btn secondary danger"
                  disabled={archivingBusy || saving || deletingBusy}
                  onClick={() => void onDelete()}
                >
                  {deletingBusy ? "Deleting…" : "Delete cohort permanently"}
                </button>
              </>
            ) : null}
          </div>
        )}
      </div>
    </dialog>
  );
}
